import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY_MAP } from 'src/constants';
import { editDeviceDetailsPromise, getDeviceDetailsPromise, LOG_ACTION_TYPE_MAP } from 'src/utils';
import { useLogAction } from '../use-log-action';
import { useGetStore } from './useGetStores';

export interface StoreDeviceHours {
  uuid: string;
  start_time?: string;
  end_time?: string;
  timezone?: string;
}

export interface BulkUpdateDeviceHoursResult {
  total: number;
  successCount: number;
}

export const useGetStoreDeviceHours = () => {
  const { data: storeData, isLoading: storeIsLoading } = useGetStore();

  const deviceIds = (storeData?.devices ?? []).map((device) => device.uuid);

  return useQuery({
    queryKey: [QUERY_KEY_MAP.useGetStoreDeviceHours, deviceIds],
    queryFn: () =>
      Promise.all(
        deviceIds.map(async (deviceId): Promise<StoreDeviceHours> => {
          const details = await getDeviceDetailsPromise(deviceId);

          return {
            uuid: deviceId,
            start_time: details?.start_time,
            end_time: details?.end_time,
            timezone: details?.timezone,
          };
        })
      ),
    enabled: !storeIsLoading && deviceIds.length > 0,
  });
};

export const useBulkUpdateDeviceHours = () => {
  const queryClient = useQueryClient();
  const { logUserAction } = useLogAction();

  const bulkUpdateDeviceHoursAsync = async (
    storeId: string,
    devices: StoreDeviceHours[],
    hours: { start_time: string; end_time: string }
  ): Promise<BulkUpdateDeviceHoursResult> => {
    const results = await Promise.allSettled(
      devices.map((device) => editDeviceDetailsPromise({ device_id: device.uuid, ...hours }))
    );

    const successCount = results.filter((res) => res.status === 'fulfilled' && res.value).length;

    await logUserAction({
      action: 'update_store_device_hours',
      action_from: JSON.stringify(devices.map(({ uuid, start_time, end_time }) => ({ uuid, start_time, end_time }))),
      action_to: JSON.stringify(hours),
      type: LOG_ACTION_TYPE_MAP.STORE_MANAGEMENT,
      type_metadata: JSON.stringify({
        uuid: storeId,
        device_ids: devices.map((device) => device.uuid),
        success_count: successCount,
      }),
    });

    void queryClient.invalidateQueries({ queryKey: [QUERY_KEY_MAP.useGetStoreDeviceHours] });
    void queryClient.invalidateQueries({ queryKey: [QUERY_KEY_MAP.useGetDeviceDetails] });
    void queryClient.invalidateQueries({ queryKey: [QUERY_KEY_MAP.useListAuditLogs] });

    return { total: devices.length, successCount };
  };

  return { bulkUpdateDeviceHoursAsync };
};
